/**
 * Watching a payment through to the draft starting.
 *
 * Renders the `CheckoutPhase` from `useCheckout`. Because the checkout opens in
 * the system browser (a separate window a task-pane cannot observe), the waiting
 * state carries a manual **"I've paid — check now"** button — the add-in's
 * substitute for the web's automatic `visibilitychange` re-poll.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
} from "@fluentui/react-components";
import type { FC } from "react";

import { openExternal } from "../services/openExternal";
import { fonts, inkColors } from "../tokens";
import type { CheckoutPhase } from "./useCheckout";

const useStyles = makeStyles({
  surface: { maxWidth: "420px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "10px" },
  text: { fontFamily: fonts.body, fontSize: "13px", color: inkColors.ink, lineHeight: 1.45 },
  muted: { fontFamily: fonts.body, fontSize: "11.5px", color: inkColors.ink3 },
  row: { display: "flex", alignItems: "center", gap: "8px" },
  caution: {
    fontFamily: fonts.body,
    fontSize: "12.5px",
    color: "#8A5B00",
    background: "#FFF6E6",
    border: "1px solid #F2D9A0",
    borderRadius: "8px",
    padding: "10px 12px",
    lineHeight: 1.45,
  },
});

/** The dialog is open for every phase except `idle`. */
export const CheckoutDialog: FC<{
  phase: CheckoutPhase;
  onCheckNow: () => void;
  onRetryDraft: () => void;
  onClose: () => void;
}> = ({ phase, onCheckNow, onRetryDraft, onClose }) => {
  const styles = useStyles();
  if (phase.kind === "idle") return null;

  // While a payment is genuinely in flight the dialog should not be dismissible
  // by backdrop/escape — closing loses the watch, not the payment.
  const watching =
    phase.kind === "opening" || phase.kind === "waiting" || phase.kind === "simulated" || phase.kind === "starting";

  return (
    <Dialog
      open
      modalType={watching ? "alert" : "modal"}
      onOpenChange={(_, data) => {
        if (!data.open && !watching) onClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{titleFor(phase)}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>{renderBody(phase, styles)}</div>
          </DialogContent>
          <DialogActions>{renderActions(phase, { onCheckNow, onRetryDraft, onClose })}</DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

function titleFor(phase: CheckoutPhase): string {
  switch (phase.kind) {
    case "opening":
      return "Opening checkout…";
    case "waiting":
    case "simulated":
      return "Finish your payment";
    case "starting":
      return "Payment received";
    case "owed":
      return "Payment received";
    case "conflict":
      return "A checkout is already open";
    case "refused":
      return phase.reason === "expired" ? "Checkout expired" : "Payment didn't go through";
    default:
      return "Something went wrong";
  }
}

function renderBody(phase: CheckoutPhase, styles: ReturnType<typeof useStyles>) {
  switch (phase.kind) {
    case "opening":
      return (
        <div className={styles.row}>
          <Spinner size="tiny" />
          <span className={styles.text}>Opening the payment page…</span>
        </div>
      );
    case "waiting":
      return (
        <>
          <span className={styles.text}>
            Finish the payment in the browser window that just opened. This panel picks it up on its own — or press the
            button below once you're done.
          </span>
          <span className={styles.muted}>Nothing is charged until you complete the checkout.</span>
        </>
      );
    case "simulated":
      return (
        <div className={styles.caution}>
          This deployment has no live payment page (the fake provider is on). Post the payment event to{" "}
          <code>/api/payment-events</code> for reference <code>{phase.handoff.ref}</code>, then press "check now".
        </div>
      );
    case "starting":
      return (
        <div className={styles.row}>
          <Spinner size="tiny" />
          <span className={styles.text}>Payment received. Starting your draft…</span>
        </div>
      );
    case "owed":
      return (
        <>
          <span className={styles.text}>
            Your payment went through, but the draft didn't start. Nothing was charged twice — press "Start the draft"
            to try again.
          </span>
          {phase.error instanceof Error && phase.error.message.length > 0 && (
            <span className={styles.muted}>Last attempt: {phase.error.message}</span>
          )}
        </>
      );
    case "conflict":
      return (
        <div className={styles.caution}>
          You already have a payment open for a different selection. Its amount won't match this order — open that one,
          or close it and try again.
        </div>
      );
    case "refused":
      return (
        <span className={styles.text}>
          {phase.reason === "expired"
            ? "That checkout expired before it was paid. Nothing was charged — try again."
            : "The payment did not go through. Nothing was charged — try again, or use a different card."}
        </span>
      );
    default:
      return (
        <span className={styles.text}>The payment couldn't be started. Nothing was charged — please try again.</span>
      );
  }
}

function renderActions(
  phase: CheckoutPhase,
  handlers: { onCheckNow: () => void; onRetryDraft: () => void; onClose: () => void },
) {
  switch (phase.kind) {
    case "opening":
    case "starting":
      return null;
    case "waiting":
    case "simulated":
      return (
        <Button appearance="primary" onClick={handlers.onCheckNow}>
          I've paid — check now
        </Button>
      );
    case "owed":
      return (
        <>
          <Button appearance="secondary" onClick={handlers.onClose}>
            Later
          </Button>
          <Button appearance="primary" onClick={handlers.onRetryDraft} disabled={phase.retrying}>
            Start the draft
          </Button>
        </>
      );
    case "conflict":
      return (
        <>
          <Button appearance="secondary" onClick={handlers.onClose}>
            Close
          </Button>
          <Button appearance="primary" onClick={() => openExternal(phase.url)}>
            Open the other checkout
          </Button>
        </>
      );
    default:
      return (
        <Button appearance="primary" onClick={handlers.onClose}>
          Close
        </Button>
      );
  }
}

export default CheckoutDialog;
